'use client';

import { useCallback, useSyncExternalStore } from 'react';
import type { EmblaCarouselType, EmblaEventType } from 'embla-carousel';

/**
 * Subscribe to an Embla carousel's events and read a single snapshot value via
 * `useSyncExternalStore`. This keeps UI state derived from Embla's imperative API
 * (e.g. `canScrollPrev`, `selectedScrollSnap`) in sync without calling `setState`
 * inside an effect — the initial value is read during render the moment `api`
 * exists, so there is no extra render/commit cycle and no flash of default state.
 *
 * Usage notes:
 * - `events` and `read` MUST be referentially stable (declare them at module
 *   scope), otherwise the subscription/snapshot is recreated on every render.
 * - `read` should return a primitive so the snapshot's identity is stable while
 *   the underlying value is unchanged. `serverValue` is used during SSR and
 *   before `api` is available.
 */
export function useEmblaSnapshot<T>(
  api: EmblaCarouselType | undefined,
  events: readonly EmblaEventType[],
  read: (api: EmblaCarouselType) => T,
  serverValue: T,
): T {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!api) return () => {};
      events.forEach((event) => api.on(event, onStoreChange));
      return () => {
        events.forEach((event) => api.off(event, onStoreChange));
      };
    },
    [api, events],
  );

  const getSnapshot = useCallback(() => (api ? read(api) : serverValue), [api, read, serverValue]);

  return useSyncExternalStore(subscribe, getSnapshot, () => serverValue);
}

export const EMBLA_NAV_EVENTS: readonly EmblaEventType[] = ['select', 'reInit'];
export const readCanScrollPrev = (embla: EmblaCarouselType): boolean => embla.canScrollPrev();
export const readCanScrollNext = (embla: EmblaCarouselType): boolean => embla.canScrollNext();
export const readSelectedSnap = (embla: EmblaCarouselType): number => embla.selectedScrollSnap();

export const EMBLA_AUTOPLAY_EVENTS: readonly EmblaEventType[] = [
  'autoplay:play',
  'autoplay:stop',
  'autoplay:select',
  'reInit',
];
export const readIsAutoplayPlaying = (embla: EmblaCarouselType): boolean =>
  embla.plugins().autoplay?.isPlaying() ?? false;
